import { V2Bookmark } from "types/bookmark";
import { ExtendedMeasureEntity, ResolvedPublicIds } from "types/misc";

import { buildResolvedIdMap } from "./idMapUtils";

/**
 * Rewrites all internal ObjectIds in a bookmark using a resolved id map.
 *
 * Ids present in the map are rewritten to their current internal id.
 * Ids not present in the map are removed – they belong to objects that no
 * longer exist in the current scene version and would point to wrong objects.
 */
export function applyIdMap(bm: V2Bookmark, resolvedPublicIds: ResolvedPublicIds): V2Bookmark {
    const map = buildResolvedIdMap(bm.publicIdMap, resolvedPublicIds);
    const resolve = (id: number | undefined): number | undefined => (id !== undefined ? map[id] : undefined);
    const resolveArr = (ids: number[]): number[] => {
        const result: number[] = [];

        ids.forEach((id) => {
            const resolved = resolve(id);

            if (resolved !== undefined) {
                result.push(resolved);
            }
        });

        return result;
    };
    const { objects, measurements, alignment, propertyTree, forms } = bm.explorerState;

    const result: V2Bookmark = {
        ...bm,
        explorerState: {
            ...bm.explorerState,
            objects: {
                ...objects,
                mainObject: {
                    ...objects.mainObject,
                    id: resolve(objects.mainObject.id),
                },
                hidden: { ...objects.hidden, ids: resolveArr(objects.hidden.ids) },
                highlighted: { ...objects.highlighted, ids: resolveArr(objects.highlighted.ids) },
                highlightCollections: Object.fromEntries(
                    Object.entries(objects.highlightCollections).map(([key, val]) => [
                        key,
                        val ? { ...val, ids: resolveArr(val.ids) } : undefined,
                    ]),
                ),
                selectionBasket: {
                    ...objects.selectionBasket,
                    ids: resolveArr(objects.selectionBasket.ids),
                },
            },
            measurements: {
                ...measurements,
                manhole: {
                    ...measurements.manhole,
                    id: resolve(measurements.manhole.id),
                    collisionTarget: (() => {
                        if (!measurements.manhole.collisionTarget) {
                            return;
                        }

                        const resolvedId = resolve(measurements.manhole.collisionTarget.selected.id);
                        return resolvedId !== undefined
                            ? { selected: { ...measurements.manhole.collisionTarget.selected, id: resolvedId } }
                            : undefined;
                    })(),
                },
                measure: {
                    ...measurements.measure,
                    entities: Array.isArray(measurements.measure.entities[0])
                        ? (measurements.measure.entities as ExtendedMeasureEntity[][]).map((arr) =>
                              arr
                                  .map((e) => {
                                      if (e.ObjectId < 0) {
                                          return e;
                                      }
                                      const resolvedId = resolve(e.ObjectId);
                                      return resolvedId !== undefined ? { ...e, ObjectId: resolvedId } : undefined;
                                  })
                                  .filter((e): e is ExtendedMeasureEntity => e !== undefined),
                          )
                        : measurements.measure.entities,
                },
                pointLine: {
                    ...measurements.pointLine,
                    ...("pointLines" in measurements.pointLine && {
                        pointLines: measurements.pointLine.pointLines?.map((line) =>
                            line.map((p) =>
                                typeof p === "object" && !Array.isArray(p) && p.objectId !== undefined
                                    ? { ...p, objectId: resolve(p.objectId) }
                                    : p,
                            ),
                        ),
                    }),
                },
            },
            ...(propertyTree
                ? {
                      propertyTree: {
                          ...propertyTree,
                          groups: propertyTree.groups.map((g) => ({
                              ...g,
                              ids: resolveArr(g.ids),
                          })),
                      },
                  }
                : {}),
            ...(alignment
                ? {
                      alignment: {
                          ...alignment,
                          followObject: alignment.followObject
                              ? {
                                    ...alignment.followObject,
                                    ids: resolveArr(alignment.followObject.ids),
                                    selectedEntity: alignment.followObject.selectedEntity
                                        ? "ObjectId" in alignment.followObject.selectedEntity
                                            ? (() => {
                                                  const objId = alignment.followObject.selectedEntity.ObjectId;
                                                  if (objId < 0) {
                                                      return alignment.followObject.selectedEntity;
                                                  }
                                                  const resolvedId = resolve(objId);
                                                  return resolvedId !== undefined
                                                      ? {
                                                            ...alignment.followObject.selectedEntity,
                                                            ObjectId: resolvedId,
                                                        }
                                                      : undefined;
                                              })()
                                            : alignment.followObject.selectedEntity
                                        : undefined,
                                }
                              : undefined,
                      },
                  }
                : {}),
            ...(forms
                ? {
                      forms: {
                          ...forms,
                          selectedFormObjectId: resolve(forms.selectedFormObjectId),
                      },
                  }
                : {}),
        },
    };

    return result;
}
